import { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { AppSidebar } from "@/components/AppSidebar";
import { EnvironmentBadge } from "@/components/EnvironmentBadge";
import { EnvironmentBorder } from "@/components/EnvironmentBorder";
import { useAuth } from "@/hooks/useAuth";

export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();
  const hadSessionRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) hadSessionRef.current = true;
    if (!isLoading && !isAuthenticated) {
      // Remember the page so login can return here after a session expiry.
      navigate("/auth", {
        replace: true,
        state: {
          from: location,
          reason: hadSessionRef.current ? "session-expired" : undefined,
        },
      });
    }
  }, [isLoading, isAuthenticated, navigate, location]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <EnvironmentBorder>
      <SidebarProvider className="h-svh min-h-0 overflow-hidden">
        <AppSidebar />
        <SidebarInset className="min-h-0 overflow-hidden">
          <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-background px-4">
            <div className="flex min-w-0 items-center gap-2">
              <SidebarTrigger className="-ml-1 shrink-0" />
              <Separator orientation="vertical" className="mr-2 h-4" />
            </div>
            <EnvironmentBadge />
          </header>
          <main className="min-h-0 flex-1 overflow-auto px-4 pb-4 pt-0">
            <div className="pt-4">
              <Outlet />
            </div>
          </main>
        </SidebarInset>
      </SidebarProvider>
    </EnvironmentBorder>
  );
}
